/**
 * Global unhandled rejection handler that catches errors at the window level
 * 
 * This works for all routes without requiring component-specific changes.
 */
export const setupFormValidationErrorHandler = () => {
  if (typeof window !== 'undefined') {
    // Store the original handler
    const originalHandler = window.onunhandledrejection;
    
    // Track navigation state
    window.__navigationInProgress = false;
    
    // Listen for navigation events
    window.addEventListener('beforeunload', () => {
      window.__navigationInProgress = true;
    });
    
    if (typeof window.history !== 'undefined') {
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;
      
      window.history.pushState = function() {
        window.__navigationInProgress = true;
        const result = originalPushState.apply(this, arguments);
        
        setTimeout(() => { 
          window.__navigationInProgress = false; 
        }, 100);
        
        return result;
      };
      
      window.history.replaceState = function() {
        window.__navigationInProgress = true;
        const result = originalReplaceState.apply(this, arguments);
        
        setTimeout(() => { 
          window.__navigationInProgress = false; 
        }, 100);
        
        return result;
      };
    }
    
    // Page visibility change can also indicate navigation
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        window.__navigationInProgress = true;
      } else {
        setTimeout(() => {
          window.__navigationInProgress = false;
        }, 100);
      }
    });
    
    // Handle unhandled rejections
    window.onunhandledrejection = function(event) {
      const error = event.reason;
      
      // Don't process if not an object
      if (!error || typeof error !== 'object') {
        if (originalHandler) return originalHandler.call(this, event);
        return;
      }
      
      // Define patterns to catch
      const isFormError = 'errorFields' in error && 'outOfDate' in error && 'values' in error;
      const isApiError = 'errorCode' in error && 'errorMessage' in error;
      const isGraphQLError = error.networkError || error.graphQLErrors;
      const isNetworkError = error.name === 'TypeError' && 
                            (error.message?.includes('fetch') || 
                             error.message?.includes('network'));
      const isFormRelatedError = error.message && typeof error.message === 'string' && 
                                (error.message.includes('validation') || 
                                 error.message.includes('form') || 
                                 error.message.includes('field'));
      
      // Prevent errors during navigation or for known patterns
      if (window.__navigationInProgress || isFormError || isApiError || 
          isGraphQLError || isNetworkError || isFormRelatedError) {
        
        console.warn('Caught unhandled rejection:', 
          error.message || error.errorMessage || 'Unknown error');
        
        // Prevent the error from being propagated and logged to Sentry
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      
      // Call original handler for other errors
      if (originalHandler) {
        return originalHandler.call(this, event);
      }
    };
  }
};